import React, { useEffect, useState } from "react";
import { Plus, Trash2, Youtube, FileText, Link as LinkIcon, Upload, X } from "lucide-react";
import { api } from "../../lib/api";

/** Reusable resources panel — used for group-level, assignment-level, and
 * chapter-level resource libraries. Pass one of:
 *   - `scope = "group"` + groupId
 *   - `scope = "assignment"` + assignmentId
 *   - `scope = "chapter"` + chapterId
 */
export default function ResourcesPanel({ scope, groupId, assignmentId, chapterId }) {
  let baseList;
  if (scope === "group") baseList = `/staff/groups/${groupId}/resources`;
  else if (scope === "assignment") baseList = `/staff/assignments/${assignmentId}/resources`;
  else baseList = `/staff/chapters/${chapterId}/resources`;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // null | "youtube" | "file" | "link"

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(baseList);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, groupId, assignmentId, chapterId]);

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete resource "${r.title}"?`)) return;
    await api.delete(`/staff/resources/${r.resource_id}`);
    load();
  };

  return (
    <section data-testid={`resources-panel-${scope}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-base font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4 text-sage" /> Resources
        </h3>
        <div className="flex items-center gap-1.5">
          <AddButton icon={Youtube} label="YouTube" onClick={() => setAdding("youtube")} testid="resources-add-youtube" />
          <AddButton icon={Upload} label="File" onClick={() => setAdding("file")} testid="resources-add-file" />
          <AddButton icon={LinkIcon} label="Link" onClick={() => setAdding("link")} testid="resources-add-link" />
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl divide-y divide-border">
        {loading && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            No resources yet. Add a YouTube video, upload a PDF, or paste a link.
          </div>
        )}
        {!loading && items.map((r) => <ResourceRow key={r.resource_id} r={r} onDelete={handleDelete} />)}
      </div>

      {adding && (
        <AddResourceModal
          type={adding}
          scope={scope}
          groupId={groupId}
          assignmentId={assignmentId}
          chapterId={chapterId}
          onClose={() => setAdding(null)}
          onSaved={() => { setAdding(null); load(); }}
        />
      )}
    </section>
  );
}

function AddButton({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 bg-white border border-border hover:border-sage/50 hover:bg-sage/5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition">
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function ResourceRow({ r, onDelete }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const fileUrl = r.file_id ? `${apiBase}/api/files/${r.file_id}` : null;
  const youtubeEmbed = r.youtube_id ? `https://www.youtube.com/embed/${r.youtube_id}` : null;

  const iconMap = {
    youtube: <Youtube className="w-4 h-4 text-[#d8382b]" />,
    file: <FileText className="w-4 h-4 text-sage" />,
    link: <LinkIcon className="w-4 h-4 text-terracotta" />,
  };
  const hrefMap = { youtube: r.youtube_url, file: fileUrl, link: r.external_url };
  const icon = iconMap[r.type] || iconMap.link;
  const href = hrefMap[r.type] || hrefMap.link;

  return (
    <div className="px-4 py-3 flex items-center gap-3" data-testid={`resource-row-${r.resource_id}`}>
      <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <a href={href} target="_blank" rel="noreferrer" className="text-sm font-medium hover:text-sage truncate block">
          {r.title}
        </a>
        {r.description && <div className="text-[11px] text-muted-foreground truncate">{r.description}</div>}
      </div>
      {r.type === "youtube" && youtubeEmbed && (
        <div className="hidden sm:block w-28 h-16 rounded-md overflow-hidden bg-black shrink-0">
          <iframe src={youtubeEmbed} title={r.title} className="w-full h-full" allowFullScreen />
        </div>
      )}
      <button onClick={() => onDelete(r)} data-testid={`resource-delete-${r.resource_id}`}
        className="w-8 h-8 rounded-lg hover:bg-terracotta/10 hover:text-terracotta text-muted-foreground">
        <Trash2 className="w-3.5 h-3.5 mx-auto" />
      </button>
    </div>
  );
}

function AddResourceModal({ type, scope, groupId, assignmentId, chapterId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [fileId, setFileId] = useState(null);
  const [filename, setFilename] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  let endpoint;
  if (scope === "group") endpoint = `/staff/groups/${groupId}/resources`;
  else if (scope === "assignment") endpoint = `/staff/assignments/${assignmentId}/resources`;
  else endpoint = `/staff/chapters/${chapterId}/resources`;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "resource");
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(r.data.file_id);
      setFilename(r.data.filename);
      if (!title) setTitle(r.data.filename);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return setErr("Please enter a title");
    if (type === "youtube" && !url.trim()) return setErr("Please paste a YouTube URL");
    if (type === "file" && !fileId) return setErr("Please upload a file");
    if (type === "link" && !url.trim()) return setErr("Please paste a URL");
    setErr("");
    setSaving(true);
    try {
      await api.post(endpoint, {
        type,
        title: title.trim(),
        description: description.trim(),
        youtube_url: type === "youtube" ? url.trim() : null,
        external_url: type === "link" ? url.trim() : null,
        file_id: type === "file" ? fileId : null,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const TYPE_LABELS = { youtube: "YouTube video", file: "File", link: "External link" };
  const typeLabel = TYPE_LABELS[type] || TYPE_LABELS.link;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="add-resource-modal">
      <div className="bg-bone w-full max-w-lg rounded-3xl border border-border overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div className="font-display text-lg font-semibold">Add {typeLabel}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary" data-testid="add-resource-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>
        <div className="px-6 py-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="add-resource-title"
              placeholder={type === "youtube" ? "e.g. Chapter 3 intro lecture" : "e.g. Lecture slides"}
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage" />
          </div>
          {type === "youtube" && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">YouTube URL</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="add-resource-youtube-url"
                placeholder="https://youtube.com/watch?v=..."
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage" />
            </div>
          )}
          {type === "link" && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">URL</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="add-resource-link-url"
                placeholder="https://…"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage" />
            </div>
          )}
          {type === "file" && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">File (≤5 MB)</label>
              <label className="mt-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-border bg-white hover:border-sage cursor-pointer transition">
                <Upload className="w-5 h-5 text-sage" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{filename || (uploading ? "Uploading…" : "Click to upload PDF, image, or document")}</div>
                  <div className="text-[11px] text-muted-foreground">PDF · DOCX · JPG · PNG · CSV · TXT · ZIP</div>
                </div>
                <input type="file" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="add-resource-file-input" />
              </label>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Description (optional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="add-resource-description"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage min-h-[50px]" />
          </div>
          {err && <div className="text-xs text-terracotta" data-testid="add-resource-error">{err}</div>}
        </div>
        <footer className="px-6 py-4 border-t border-border bg-white flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-sage" data-testid="add-resource-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving || uploading} data-testid="add-resource-save"
            className="bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-5 py-2 rounded-lg transition-all">
            {saving ? "Saving…" : "Add"}
          </button>
        </footer>
      </div>
    </div>
  );
}
